/**
 * App-wide behaviour settings (product / supplierProduct / supplierProductItem
 * naming rules), as a plain function with no framework dependency.
 *
 * WHY THIS IS NOT ONLY A PLUGIN: the callers are input-field spec modules
 * (`services/entityInputFieldsService/modules/*.ts`) — plain modules, not
 * components — so they cannot reach a Nuxt-provided helper. Under Nuxt 2 they
 * read the global `window.$nuxt.$getApplicationSettings()`, which **Nuxt 4
 * removes**; the optional-chained reads then silently produced `undefined` and
 * flipped their guards. Owning the values here and letting
 * `plugins/getApplicationSettings.ts` delegate is the same shape already used
 * for the formatters (`cynqueModules/formatDollars.ts`,
 * `cynqueModules/utils/format24As12.ts`, ...): one implementation, imported
 * directly by non-component code and re-exposed as `$getApplicationSettings`
 * for templates.
 *
 * These are hard-coded today. If they ever become user- or API-driven, this is
 * the single place to change, and callers keep working.
 */

import { ItemProductsInSupplierProductProductsSelectListIds } from '~/services/selectLists/itemProductsInSupplierProductProductsSelectList'

interface IApplicationProductSettings {
  doAppendSetOrKitToProductName: boolean
}

interface IApplicationSupplierProductSettings {
  doPrependSupplierNameToSupplierProductName: boolean
  doStripSetOrKitStringsFromSourceProductName: boolean
  // Which ITEM-level products the create-supplier-product dialog's Product list
  // offers (kits are always offered). One three-valued setting rather than two
  // overlapping booleans, so no combination is redundant.
  itemProductsInSupplierProductProductsSelectList: ItemProductsInSupplierProductProductsSelectListIds
}

interface IApplicationSupplierProductItemSettings {
  doPrependSupplierNameToSupplierProductItemName: boolean
  doStripSetOrKitStringsFromSourceProductName: boolean
}

// The first non-entity group: these settings are scoped to a COMPONENT rather
// than to an entity, so they get their own bucket instead of being flattened in
// beside product / supplierProduct / supplierProductItem.
interface IApplicationTableCardSettings {
  // Suppress the upper-casing of every TableCard highlighted-attribute cell,
  // app-wide. Individual cards can still opt out on their own via the
  // attributeToHighlightNoUcase / attribute2ToHighlightNoUcase props — the two
  // are OR'd together in TableCard's getHighlightedAttribute.
  doNotUCaseAnyTableCardAttributesToHighlight: boolean
}

interface ITenantSettings {
  descr: string
  nickname: string
  websiteUrl: string
}

export interface IApplicationSettings {
  product: IApplicationProductSettings
  supplierProduct: IApplicationSupplierProductSettings
  supplierProductItem: IApplicationSupplierProductItemSettings
  tableCard: IApplicationTableCardSettings
  tenant: ITenantSettings
}

export function getApplicationSettings(): IApplicationSettings {
  return {
    product: {
      doAppendSetOrKitToProductName: true,
    },
    supplierProduct: {
      doPrependSupplierNameToSupplierProductName: false,
      doStripSetOrKitStringsFromSourceProductName: true,
      itemProductsInSupplierProductProductsSelectList:
        ItemProductsInSupplierProductProductsSelectListIds.NONE,
    },
    supplierProductItem: {
      doPrependSupplierNameToSupplierProductItemName: false,
      doStripSetOrKitStringsFromSourceProductName: true,
    },
    tableCard: {
      doNotUCaseAnyTableCardAttributesToHighlight: true,
    },
    tenant: {
      descr: 'WX Technology Group',
      nickname: 'WXTG',
      websiteUrl: 'wxtechnologygroup.com',
    },
  }
}
